import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { useRef, useEffect, useCallback } from 'react';

import { useInventoryData } from 'src/utils/guards/use-inventory-data';
import type { BusinessDeviceSyncFields } from 'src/utils/Services/device-sync-v4.service';
import { startBusinessDocumentListener } from 'src/utils/Services/business-document-listener.service';

import { useDeviceSyncStore } from 'src/store/DeviceSync';
import { useConnectivityStore } from 'src/features/connectivity';

import { useSnackbar } from 'src/components/snackbar';

type Props = {
  children: React.ReactNode;
  enableInventoryData?: boolean;
  enableInventorySync?: boolean;
  enableDeviceControl?: boolean;
  enableDeviceSync?: boolean;
  onDeviceSyncFieldsChange?: (fields: BusinessDeviceSyncFields) => Promise<void>;
  showUpdateSnackbar?: boolean;
  businessIdOverride?: string | null;
  onProviderDetailsVersionChange?: (version: number) => void;
};

export default function BusinessDocumentListenerProvider({
  children,
  enableInventoryData = true,
  enableInventorySync = true,
  enableDeviceControl = true,
  enableDeviceSync = true,
  onDeviceSyncFieldsChange,
  showUpdateSnackbar = true,
  businessIdOverride,
  onProviderDetailsVersionChange,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const syncedBusinessId = useDeviceSyncStore((state) => state.businessData?.id);
  const businessId = businessIdOverride ?? syncedBusinessId;
  const isUpdating = useDeviceSyncStore((state) => state.status === 'syncing');
  const isOnline = useConnectivityStore((state) => state.runtimeState === 'ONLINE');
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useInventoryData(enableInventoryData && isOnline);

  useEffect(() => {
    if (!showUpdateSnackbar) return;
    if (isUpdating) {
      enqueueSnackbar('Updating your account data…', { variant: 'info' });
    }
  }, [isUpdating, enqueueSnackbar, showUpdateSnackbar]);

  const startListener = useCallback(() => {
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
    if (!businessId || !isOnline) return;
    unsubscribeRef.current = startBusinessDocumentListener(businessId, {
      enableInventorySync,
      enableDeviceControl,
      enableDeviceSync,
      onDeviceSyncFieldsChange,
      onProviderDetailsVersionChange,
    });
  }, [
    businessId,
    enableInventorySync,
    enableDeviceControl,
    enableDeviceSync,
    isOnline,
    onProviderDetailsVersionChange,
    onDeviceSyncFieldsChange,
  ]);

  // Start/restart on mount and whenever businessId changes.
  useEffect(() => {
    startListener();
    return () => {
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
    };
  }, [startListener]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') startListener();
    };
    window.addEventListener('online', startListener);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online', startListener);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [startListener]);

  // On native, restart the listener when the app comes back to the foreground so
  // the force logout check fires immediately rather than waiting for Firestore to
  // re-establish its WebSocket connection.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    const listenerPromise = App.addListener('appStateChange', (state) => {
      if (state.isActive) {
        startListener();
      }
    });

    return () => {
      listenerPromise.then((l) => l.remove());
    };
  }, [startListener]);

  return <>{children}</>;
}
